import { Users, Edit, Save, X, Plus, Trash2 } from 'lucide-react';
import { HeroSection } from '../HeroSection';
import { useState, useEffect } from 'react';
import { projectOrgService } from '../../services/projectOrg';
import { toast } from 'sonner';

interface TeamMember {
  name: string;
  role: string;
}

interface ProjectTeam {
  manager: string;
  members: TeamMember[];
}

interface OrgStructure {
  sponsor: { name: string; title: string };
  projectManager: { name: string; title: string };
  teams: ProjectTeam[];
}

// Demo data — rendered ONLY when projectId is unset (preview / marketing mode).
// Real projects must never see these names; falling through to mock data during
// an outage or on a fresh project violates feedback_silent_fallback.
// Empty skeleton used as the initial state and the fallback for fresh / failed
// loads on real projects. Renders an editable structure with no mock people.
const EMPTY_ORG: OrgStructure = {
  sponsor: { name: '', title: 'Project Owner / Sponsor' },
  projectManager: { name: '', title: 'Project Manager' },
  teams: [],
};

function mergeOrg(incoming: OrgStructure | null | undefined): OrgStructure {
  // Backend returns org_structure: null for projects that have never been
  // edited. Return EMPTY_ORG — never sample people.
  if (!incoming || (!incoming.sponsor && !incoming.projectManager && !Array.isArray(incoming.teams))) {
    return EMPTY_ORG;
  }
  return {
    sponsor: {
      name: incoming.sponsor?.name ?? '',
      title: incoming.sponsor?.title ?? EMPTY_ORG.sponsor.title,
    },
    projectManager: {
      name: incoming.projectManager?.name ?? '',
      title: incoming.projectManager?.title ?? EMPTY_ORG.projectManager.title,
    },
    teams: Array.isArray(incoming.teams)
      ? incoming.teams.map((t) => ({
          manager: t.manager ?? '',
          members: Array.isArray(t.members)
            ? t.members.map((m) => ({ name: m?.name ?? '', role: m?.role ?? '' }))
            : [],
        }))
      : [],
  };
}

interface ProjectOrgSectionProps {
  projectId?: number;
}

export function ProjectOrgSection({ projectId }: ProjectOrgSectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  // Real projects start empty; demo data is reserved for the !projectId branch.
  const [orgData, setOrgData] = useState<OrgStructure>(EMPTY_ORG);

  useEffect(() => {
    if (!projectId) {
      setOrgData(EMPTY_ORG);
      return;
    }
    projectOrgService
      .get(projectId)
      .then((r) => setOrgData(mergeOrg(r.org_structure as OrgStructure)))
      .catch(() => {
        toast.error('Failed to load project organization');
        // Stay empty — never fall back to demo names for a real project.
        setOrgData(EMPTY_ORG);
      });
  }, [projectId]);

  const [editedOrgData, setEditedOrgData] = useState<OrgStructure>(orgData);
  useEffect(() => {
    setEditedOrgData(orgData);
  }, [orgData]);

  const handleEdit = () => {
    setEditedOrgData(JSON.parse(JSON.stringify(orgData))); // Deep clone
    setIsEditing(true);
  };

  const handleSave = () => {
    if (projectId) {
      projectOrgService
        .update(projectId, editedOrgData)
        .then(() => {
          setOrgData(editedOrgData);
          setIsEditing(false);
          setNotification({ message: 'Organization structure updated successfully', type: 'success' });
          setTimeout(() => setNotification(null), 3000);
        })
        .catch(() => {
          toast.error('Failed to save organization structure');
        });
    } else {
      setOrgData(editedOrgData);
      setIsEditing(false);
      setNotification({ message: 'Organization structure updated successfully', type: 'success' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handleCancel = () => {
    setEditedOrgData(orgData);
    setIsEditing(false);
    setNotification({ message: 'Changes discarded', type: 'info' });
    setTimeout(() => setNotification(null), 3000);
  };

  const updateSponsor = (field: 'name' | 'title', value: string) => {
    setEditedOrgData({
      ...editedOrgData,
      sponsor: { ...editedOrgData.sponsor, [field]: value }
    });
  };

  const updateProjectManager = (field: 'name' | 'title', value: string) => {
    setEditedOrgData({
      ...editedOrgData,
      projectManager: { ...editedOrgData.projectManager, [field]: value }
    });
  };

  const updateTeamManager = (teamIndex: number, value: string) => {
    const newTeams = [...editedOrgData.teams];
    newTeams[teamIndex].manager = value;
    setEditedOrgData({ ...editedOrgData, teams: newTeams });
  };

  const updateTeamMember = (teamIndex: number, memberIndex: number, field: 'name' | 'role', value: string) => {
    const newTeams = [...editedOrgData.teams];
    newTeams[teamIndex].members[memberIndex][field] = value;
    setEditedOrgData({ ...editedOrgData, teams: newTeams });
  };

  const addTeamMember = (teamIndex: number) => {
    const newTeams = [...editedOrgData.teams];
    newTeams[teamIndex].members.push({ name: 'New Member', role: 'Role' });
    setEditedOrgData({ ...editedOrgData, teams: newTeams });
  };

  const removeTeamMember = (teamIndex: number, memberIndex: number) => {
    const newTeams = [...editedOrgData.teams];
    newTeams[teamIndex].members.splice(memberIndex, 1);
    setEditedOrgData({ ...editedOrgData, teams: newTeams });
  };

  const addTeam = () => {
    const newTeams = [...editedOrgData.teams];
    newTeams.push({
      manager: 'New Manager',
      members: [
        { name: 'Team Member 1', role: 'Role' },
      ]
    });
    setEditedOrgData({ ...editedOrgData, teams: newTeams });
  };

  const removeTeam = (teamIndex: number) => {
    const newTeams = editedOrgData.teams.filter((_, index) => index !== teamIndex);
    setEditedOrgData({ ...editedOrgData, teams: newTeams });
  };

  const displayData = isEditing ? editedOrgData : orgData;
  const totalMembers = 2 + displayData.teams.length + displayData.teams.reduce((sum, team) => sum + team.members.length, 0);
  const totalTeamLeads = displayData.teams.length;
  const totalTeamMembers = displayData.teams.reduce((sum, team) => sum + team.members.length, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-2xl border-2 flex items-center gap-3 animate-in slide-in-from-top-5 ${
          notification.type === 'success' ? 'bg-green-500 border-green-600 text-white' :
          notification.type === 'error' ? 'bg-red-500 border-red-600 text-white' :
          'bg-blue-500 border-blue-600 text-white'
        }`}>
          <span className="font-medium">{notification.message}</span>
          <button 
            onClick={() => setNotification(null)}
            className="ml-2 hover:bg-white/20 rounded p-1 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Hero Section */}
      <HeroSection
        title="Project Organization Hierarchy"
        description="Visual representation of the project team structure, reporting lines, and role distribution"
        variant="level4"
        metrics={[
          { label: 'Total Team Members', value: totalMembers.toString(), icon: Users, iconColor: 'bg-blue-50' },
          { label: 'Team Leads', value: totalTeamLeads.toString(), icon: Users, iconColor: 'bg-amber-50' },
          { label: 'Team Members', value: totalTeamMembers.toString(), icon: Users, iconColor: 'bg-green-50' },
          { label: 'Leadership', value: '2', icon: Users, iconColor: 'bg-red-50' },
        ]}
      />

      {/* Organization Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-xl text-neutral-950 mb-2">Project Organization Structure</h2>
            <p className="text-sm text-gray-600">
              {isEditing 
                ? 'Edit the organization structure by clicking on the fields below'
                : 'Hierarchical view of the project team with sponsor, project manager, and their respective teams'
              }
            </p>
          </div>
          
          {/* Edit Controls */}
          <div className="flex gap-3">
            {!isEditing ? (
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5C748E] transition-all flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit Structure
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Changes
                </button>
              </>
            )}
          </div>
        </div>

        {/* Org Chart Container */}
        <div className="flex flex-col items-center">
          {/* Sponsor Level */}
          <div className="mb-8">
            {isEditing ? (
              <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-8 py-4 rounded-lg shadow-lg min-w-[300px]">
                <input
                  type="text"
                  value={editedOrgData.sponsor.title}
                  onChange={(e) => updateSponsor('title', e.target.value)}
                  className="w-full bg-white/20 text-white placeholder-white/70 px-3 py-2 rounded font-semibold text-lg text-center mb-2 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
                <input
                  type="text"
                  value={editedOrgData.sponsor.name}
                  onChange={(e) => updateSponsor('name', e.target.value)}
                  className="w-full bg-white/20 text-white placeholder-white/70 px-3 py-2 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>
            ) : (
              <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-8 py-4 rounded-lg shadow-lg min-w-[300px] text-center">
                <div className="font-semibold text-lg">{displayData.sponsor.title}</div>
                <div className="text-sm mt-1 opacity-90">{displayData.sponsor.name}</div>
              </div>
            )}
          </div>

          {/* Connecting Line from Sponsor */}
          <div className="w-0.5 h-12 bg-gray-300"></div>

          {/* Project Manager Level */}
          <div className="mb-8">
            {isEditing ? (
              <div className="bg-gradient-to-r from-[#DAA520] to-[#c99a1c] text-white px-8 py-4 rounded-lg shadow-lg min-w-[300px]">
                <input
                  type="text"
                  value={editedOrgData.projectManager.title}
                  onChange={(e) => updateProjectManager('title', e.target.value)}
                  className="w-full bg-white/20 text-white placeholder-white/70 px-3 py-2 rounded font-semibold text-center mb-2 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
                <input
                  type="text"
                  value={editedOrgData.projectManager.name}
                  onChange={(e) => updateProjectManager('name', e.target.value)}
                  className="w-full bg-white/20 text-white placeholder-white/70 px-3 py-2 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>
            ) : (
              <div className="bg-gradient-to-r from-[#DAA520] to-[#c99a1c] text-white px-8 py-4 rounded-lg shadow-lg min-w-[300px] text-center">
                <div className="font-semibold text-lg">{displayData.projectManager.title}</div>
                <div className="text-sm mt-1">{displayData.projectManager.name}</div>
              </div>
            )}
          </div>

          {/* Connecting Line to Teams */}
          <div className="w-0.5 h-12 bg-gray-300"></div>

          {/* Horizontal Line for Team Leads */}
          <div className="relative w-full max-w-[1600px] mb-12">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gray-300"></div>
            
            {/* Team Leads Grid */}
            <div className={`grid gap-6 pt-12`} style={{ gridTemplateColumns: `repeat(${displayData.teams.length}, 1fr)` }}>
              {displayData.teams.map((team, teamIndex) => (
                <div key={teamIndex} className="flex flex-col items-center">
                  {/* Vertical Line to Team Lead */}
                  <div className="w-0.5 h-12 bg-gray-300 -mt-12"></div>
                  
                  {/* Team Lead Box */}
                  <div className="relative bg-gradient-to-r from-[#486581] to-[#5C748E] text-white px-6 py-3 rounded-lg shadow-md w-full mb-6">
                    {isEditing ? (
                      <div className="text-center">
                        <div className="font-semibold mb-2">Team Lead</div>
                        <input
                          type="text"
                          value={team.manager}
                          onChange={(e) => updateTeamManager(teamIndex, e.target.value)}
                          className="w-full bg-white/20 text-white placeholder-white/70 px-3 py-2 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-white/50"
                        />
                        {displayData.teams.length > 1 && (
                          <button
                            onClick={() => removeTeam(teamIndex)}
                            className="absolute -top-2 -right-2 p-1 bg-red-600 rounded-full hover:bg-red-700 transition-all shadow-lg"
                            title="Remove Team"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="font-semibold">Team Lead</div>
                        <div className="text-sm mt-1">{team.manager}</div>
                      </div>
                    )}
                  </div>

                  {/* Team Members */}
                  <div className="w-full space-y-3">
                    {team.members.map((member, memberIndex) => (
                      <div key={memberIndex} className="relative">
                        {/* Connecting Line */}
                        <div className="absolute -left-6 top-1/2 w-6 h-0.5 bg-gray-300"></div>
                        
                        {/* Member Box */}
                        {isEditing ? (
                          <div className="relative bg-gradient-to-r from-[#8B9F6E] to-[#7A8E5D] text-white px-4 py-2.5 rounded-lg shadow-sm">
                            <input
                              type="text"
                              value={member.role}
                              onChange={(e) => updateTeamMember(teamIndex, memberIndex, 'role', e.target.value)}
                              className="w-full bg-white/20 text-white placeholder-white/70 px-2 py-1 rounded text-sm font-medium mb-1 focus:outline-none focus:ring-2 focus:ring-white/50"
                            />
                            <input
                              type="text"
                              value={member.name}
                              onChange={(e) => updateTeamMember(teamIndex, memberIndex, 'name', e.target.value)}
                              className="w-full bg-white/20 text-white placeholder-white/70 px-2 py-1 rounded text-xs focus:outline-none focus:ring-2 focus:ring-white/50"
                            />
                            {team.members.length > 1 && (
                              <button
                                onClick={() => removeTeamMember(teamIndex, memberIndex)}
                                className="absolute -top-2 -right-2 p-1 bg-red-600 rounded-full hover:bg-red-700 transition-all shadow-lg"
                                title="Remove Member"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="bg-gradient-to-r from-[#8B9F6E] to-[#7A8E5D] text-white px-4 py-2.5 rounded-lg shadow-sm">
                            <div className="text-sm font-medium">{member.role}</div>
                            <div className="text-xs mt-0.5 opacity-90">{member.name}</div>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {/* Add Member Button */}
                    {isEditing && (
                      <button
                        onClick={() => addTeamMember(teamIndex)}
                        className="w-full py-2 border-2 border-dashed border-[#8B9F6E] text-[#8B9F6E] rounded-lg hover:bg-[#8B9F6E]/10 transition-all flex items-center justify-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add Member
                      </button>
                    )}
                  </div>

                  {/* Vertical Line from Team Lead to First Member */}
                  <div className="absolute w-0.5 bg-gray-300" style={{
                    left: `calc(${(teamIndex / displayData.teams.length * 100) + (50 / displayData.teams.length)}% - 1px)`,
                    top: '92px',
                    height: '24px'
                  }}></div>
                  
                  {/* Horizontal Line for Team Members */}
                  <div className="absolute bg-gray-300 h-0.5" style={{
                    left: `calc(${(teamIndex / displayData.teams.length * 100) + (50 / displayData.teams.length)}% - 1px)`,
                    width: '24px',
                    top: '116px',
                    transform: 'translateX(-24px)'
                  }}></div>
                </div>
              ))}
            </div>
          </div>

          {/* Add Team Button */}
          {isEditing && (
            <button
              onClick={addTeam}
              className="mt-6 px-6 py-3 bg-[#486581] text-white rounded-lg hover:bg-[#5C748E] transition-all flex items-center gap-2 shadow-md"
            >
              <Plus className="w-5 h-5" />
              Add New Team
            </button>
          )}
        </div>
      </div>

      {/* Role Definitions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-xl text-neutral-950 mb-4">PMI-Aligned Role Definitions</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg p-4 bg-red-50/30">
            <h3 className="text-base text-neutral-950 mb-2 font-semibold">Project Owner / Sponsor</h3>
            <p className="text-sm text-gray-600">
              Provides strategic direction, secures funding, removes organizational barriers, approves major decisions, and ensures project alignment with business objectives
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 bg-amber-50/30">
            <h3 className="text-base text-neutral-950 mb-2 font-semibold">Project Manager</h3>
            <p className="text-sm text-gray-600">
              Overall responsibility for project planning, execution, monitoring, and delivery within scope, schedule, and budget constraints. Manages team and stakeholders
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 bg-blue-50/30">
            <h3 className="text-base text-neutral-950 mb-2 font-semibold">Team Lead</h3>
            <p className="text-sm text-gray-600">
              Manages specific functional team, coordinates team activities, reports to Project Manager, ensures team deliverables meet quality standards
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 bg-green-50/30">
            <h3 className="text-base text-neutral-950 mb-2 font-semibold">Business/Requirements/Data Analyst</h3>
            <p className="text-sm text-gray-600">
              Elicits, analyzes, and documents business requirements, ensures alignment with business objectives, and facilitates communication between business and technical teams
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 bg-green-50/30">
            <h3 className="text-base text-neutral-950 mb-2 font-semibold">Solution/Technical/Enterprise Architect</h3>
            <p className="text-sm text-gray-600">
              Provides technical direction, architecture decisions, ensures scalability and maintainability, defines technical standards, and reviews design patterns
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 bg-green-50/30">
            <h3 className="text-base text-neutral-950 mb-2 font-semibold">Developer (All Types)</h3>
            <p className="text-sm text-gray-600">
              Develops software solutions, writes clean and maintainable code, participates in code reviews, implements features, and fixes defects
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 bg-green-50/30">
            <h3 className="text-base text-neutral-950 mb-2 font-semibold">QA Lead / QA Engineer / Tester</h3>
            <p className="text-sm text-gray-600">
              Creates test plans and test cases, executes manual and automated tests, identifies defects, ensures deliverables meet quality standards and acceptance criteria
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 bg-green-50/30">
            <h3 className="text-base text-neutral-950 mb-2 font-semibold">Subject Matter Expert (SME)</h3>
            <p className="text-sm text-gray-600">
              Provides domain expertise, validates requirements, participates in solution design, and ensures deliverables meet business needs and industry standards
            </p>
          </div>
        </div>
      </div>

      {/* Team Statistics */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-base text-neutral-950 mb-4 font-semibold">Reporting Structure</h3>
          <div className="text-3xl font-bold text-[#486581] mb-2">3</div>
          <p className="text-sm text-gray-600">Levels in hierarchy (Sponsor → PM → Teams)</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-base text-neutral-950 mb-4 font-semibold">Teams</h3>
          <div className="text-3xl font-bold text-[#486581] mb-2">{displayData.teams.length}</div>
          <p className="text-sm text-gray-600">Functional teams reporting to Project Manager</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-base text-neutral-950 mb-4 font-semibold">Avg Team Size</h3>
          <div className="text-3xl font-bold text-[#8B9F6E] mb-2">
            {displayData.teams.length > 0 ? Math.round(totalTeamMembers / displayData.teams.length) : 0}
          </div>
          <p className="text-sm text-gray-600">Average members per functional team</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-base text-neutral-950 mb-4 font-semibold">Span of Control</h3>
          <div className="text-3xl font-bold text-[#8B9F6E] mb-2">{totalTeamLeads}</div>
          <p className="text-sm text-gray-600">Direct reports to Project Manager</p>
        </div>
      </div>
    </div>
  );
}
